import { PageWithTitle } from 'src/Common/components';
import styled from '@emotion/styled';
import { Box, Stack, Tab, Tabs, Typography } from '@mui/material';
import useLocalStorageState from 'use-local-storage-state';
import { LiveBadge } from 'src/Common/components/LiveBadge/LiveBadge';
import { ToggleUpdatesButton } from 'src/Common/components/ToggleUpdatesButton/ToggleUpdatesButton';
import { usePredictions } from '../context';
import { PredictionsTable, CategoryFilter } from '../components';

type OpportunitiesTab = 'active' | 'archived';

const PredictionsPageContent = () => {
  const {
    opportunities,
    archivedOpportunities,
    isConnected,
    count,
    archivedCount,
  } = usePredictions();

  const [tab, setTab] = useLocalStorageState<OpportunitiesTab>(
    'V3.Predictions.opportunitiesTab',
    { defaultValue: 'active' }
  );

  const data = tab === 'archived' ? archivedOpportunities : opportunities;
  // Only show the loader on the Active tab — the Archived tab is not "waiting
  // for the first WS payload"; it is empty until the user archives something.
  const isLoading = tab === 'active' && isConnected && opportunities.length === 0;

  return (
    <PageWithTitle>
      <Stack direction="row">
        <Box style={{ width: '100%' }}>
          <Toolbar>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Stack direction="row" spacing={2} alignItems="center">
                <Typography
                  variant="h6"
                  color="text.primary"
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  Opportunities
                </Typography>
              </Stack>
              <Stack direction="row" spacing={2} alignItems="center">
                <CategoryFilter />
                <LiveBadge isLive={isConnected} />
                <ToggleUpdatesButton subsystem="predictions" />
              </Stack>
            </Box>
            <Tabs
              value={tab}
              onChange={(_, v: OpportunitiesTab) => setTab(v)}
              sx={{
                mt: 1,
                minHeight: 32,
                '& .MuiTab-root': {
                  minHeight: 32,
                  py: 0,
                  textTransform: 'none',
                  fontSize: '0.82rem',
                },
                '& .MuiTabs-indicator': { height: 2 },
              }}
            >
              <Tab value="active" label={`Active (${count})`} />
              <Tab value="archived" label={`Archived (${archivedCount})`} />
            </Tabs>
          </Toolbar>

          <PredictionsTable data={data} isLoading={isLoading} />
        </Box>
      </Stack>
    </PageWithTitle>
  );
};

export const PredictionsPage = () => {
  return <PredictionsPageContent />;
};

const Toolbar = styled.div({
  display: 'flex',
  flexDirection: 'column',
  width: '100%'
});
